// src/components/PurchaseOrderPrintView.jsx
import React, { useEffect } from 'react';
import { FileText, Store, Building, Calendar } from 'lucide-react';

const PurchaseOrderPrintView = ({ purchaseOrder, vendorName, onClose }) => {
    useEffect(() => {
        // Automatically trigger print dialog when mounted
        window.print();
    }, []);

    if (!purchaseOrder) return null;

    return (
        <div className="fixed inset-0 bg-white z-50 p-8 overflow-y-auto text-black print:p-0">
            {/* Print Controls / Close Button (Hidden when printing) */}
            <div className="flex justify-between items-center mb-6 pb-4 border-b print:hidden">
                <button
                    onClick={() => window.print()}
                    className="bg-blue-600 text-white px-4 py-2 rounded shadow font-semibold hover:bg-blue-700"
                >
                    Print / Save as PDF
                </button>
                <button
                    onClick={onClose}
                    className="bg-gray-200 text-gray-800 px-4 py-2 rounded font-semibold hover:bg-gray-300"
                >
                    Close Preview
                </button>
            </div>

            {/* Printable Document Content */}
            <div className="max-w-3xl mx-auto bg-white p-6">
                {/* Header */}
                <div className="flex justify-between items-start border-b pb-6 mb-6">
                    <div>
                        <h1 className="text-2xl font-bold uppercase tracking-wide text-gray-900">Purchase Order</h1>
                        <p className="text-sm text-gray-600 mt-1">PO Number: <span className="font-mono font-bold text-black">{purchaseOrder.ponumber}</span></p>
                    </div>
                    <div className="text-right">
                        <p className="text-sm font-semibold">Status: {purchaseOrder.openindicator === 'Y' ? 'Open' : 'Closed'}</p>
                        <p className="text-xs text-gray-500 mt-1">
                            Date: {new Date(purchaseOrder.orderdate).toLocaleDateString()}
                        </p>
                    </div>
                </div>

                {/* Vendor & Store Information */}
                <div className="grid grid-cols-2 gap-6 mb-8 text-sm">
                    <div className="border p-4 rounded-lg bg-gray-50 print:bg-transparent">
                        <h3 className="font-bold text-gray-700 uppercase text-xs mb-2">Vendor Details</h3>
                        <p className="font-semibold text-base">{vendorName || purchaseOrder.vendor?.vendorname}</p>
                        <p className="text-gray-600 text-xs mt-1">Vendor ID: {purchaseOrder.vendor_id}</p>
                    </div>
                    <div className="border p-4 rounded-lg bg-gray-50 print:bg-transparent">
                        <h3 className="font-bold text-gray-700 uppercase text-xs mb-2">Store / Destination</h3>
                        <p className="font-semibold text-base">{purchaseOrder.store?.identifier || 'N/A'}</p>
                        <p className="text-gray-600 text-xs mt-1">Currency: {purchaseOrder.store?.setccurr || 'NGN'}</p>
                    </div>
                </div>

                {/* Items Table */}
                <div className="mb-8">
                    <h3 className="font-bold text-sm uppercase text-gray-700 mb-3">Order Items</h3>
                    <table className="w-full text-left border-collapse text-sm">
                        <thead>
                            <tr className="border-b-2 border-gray-300 bg-gray-100 print:bg-gray-200">
                                <th className="p-2 font-semibold">Product Name</th>
                                <th className="p-2 font-semibold">Part Number</th>
                                <th className="p-2 font-center text-center font-semibold">Ordered</th>
                                <th className="p-2 font-center text-center font-semibold">Received</th>
                                <th className="p-2 font-center text-center font-semibold">Remaining</th>
                                <th className="p-2 font-semibold">Expected Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {purchaseOrder.poitems?.map((item, index) => (
                                <tr key={index} className="border-b border-gray-200">
                                    <td className="p-2 font-medium">{item.catentry?.productname}</td>
                                    <td className="p-2 font-mono text-xs text-gray-600">{item.catentry?.partnumber}</td>
                                    <td className="p-2 text-center">{item.qtyordered}</td>
                                    <td className="p-2 text-center">{item.qtyreceived}</td>
                                    <td className="p-2 text-center font-semibold text-orange-600">{item.qtyremaining}</td>
                                    <td className="p-2">{item.expecteddate || 'N/A'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Footer summary */}
                <div className="border-t pt-4 text-xs text-gray-500 flex justify-between">
                    <span>Purchase Order ID: {purchaseOrder.purchaseorder_id}</span>
                    <span>System Generated Document</span>
                </div>
            </div>
        </div>
    );
};

export default PurchaseOrderPrintView;